import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowRight } from '@phosphor-icons/react'
import { apiRequest } from '../api/client'
import type { ActivePlan, Dashboard, PlanItem } from '../api/types'
import { formatDate, formatDuration, formatPrescription, localIsoDate, workoutStatusLabel } from '../app/format'
import { navigate } from '../app/navigation'
import { Link } from '../components/Link'
import { queryClient } from '../app/queryClient'
import { EmptyState, ErrorState, PageLoader } from '../components/States'
import { dashboardWeekState } from './dashboardWeek'

const weekDays = [
  { key: 'Monday', short: 'PO' },
  { key: 'Tuesday', short: 'ÚT' },
  { key: 'Wednesday', short: 'ST' },
  { key: 'Thursday', short: 'ČT' },
  { key: 'Friday', short: 'PÁ' },
  { key: 'Saturday', short: 'SO' },
  { key: 'Sunday', short: 'NE' },
]

const dayKeyByNumber = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function addDays(value: Date, days: number): Date {
  const next = new Date(value)
  next.setDate(next.getDate() + days)
  return next
}

function toLocalIso(value: Date): string {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function itemSetCount(item: PlanItem): number {
  if (item.prescriptionBlocks.length > 0) {
    return item.prescriptionBlocks.reduce((sum, block) => sum + block.targets.length, 0)
  }
  return item.targets.length
}

export function DashboardPage() {
  const date = localIsoDate()
  const dashboard = useQuery({
    queryKey: ['dashboard', date],
    queryFn: () => apiRequest<Dashboard>(`/dashboard?date=${date}`),
  })
  const activePlan = useQuery({
    queryKey: ['active-plan'],
    queryFn: () => apiRequest<ActivePlan>('/plans/active'),
    enabled: Boolean(dashboard.data?.activePlan),
  })
  const start = useMutation({
    mutationFn: () => apiRequest(`/workouts/${date}/start`, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      navigate(`/workout/${date}`)
    },
  })

  if (dashboard.isPending) return <PageLoader label="Připravuji dnešní přehled…" />
  if (dashboard.isError) return <ErrorState error={dashboard.error} onRetry={() => dashboard.refetch()} />

  const data = dashboard.data
  const hasToday = Boolean(data.today)
  const todayStatus = data.today?.status ?? null
  const plan = activePlan.data
  const todayDate = new Date(`${date}T12:00:00`)
  const todayDayKey = dayKeyByNumber[todayDate.getDay()]
  const todayItems = (plan?.items ?? [])
    .filter((item) => item.dayOfWeek === todayDayKey)
    .sort((a, b) => a.sortOrder - b.sortOrder)
  const todaySetCount = todayItems.reduce((sum, item) => sum + itemSetCount(item), 0)
  const visibleRecentWorkouts = data.recentWorkouts.filter((workout) => workout.status !== 'Planned')
  const todayName = todayItems[0]?.dayName ?? (data.activePlan ? 'Volný den' : 'Bez aktivního plánu')
  const monday = addDays(todayDate, -((todayDate.getDay() + 6) % 7))
  const week = weekDays.map((day, index) => {
    const localDate = toLocalIso(addDays(monday, index))
    const workout = data.recentWorkouts.find((item) => item.localDate === localDate)
    const planned = (plan?.items ?? []).some((item) => item.dayOfWeek === day.key)
    const isToday = localDate === date
    const state = dashboardWeekState(workout?.status ?? null, planned, isToday, localDate < date)
    return { ...day, localDate, planned, state }
  })

  return (
    <div className="page-stack dashboard-page">
      <div className="screen-kicker">
        <span>01 / Dnes</span>
        <time dateTime={date}>{formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' })}</time>
      </div>

      <header className="dashboard-hero">
        <div>
          <h1>{todayName}</h1>
          <p className="dashboard-hero__focus">{data.activePlan?.name ?? 'Vyber aktivní plán'}</p>
          <p className="dashboard-hero__meta">
            {todayItems.length > 0
              ? `${todayItems.length} cviků / ${todaySetCount} sérií`
              : data.activePlan ? 'Dnes bez předepsaného tréninku' : 'Plán zatím není aktivní'}
          </p>
        </div>
        <div className="dashboard-hero__action">
          {todayStatus === 'InProgress' ? (
            <Link to={'/workout/' + date} className="button button--primary dashboard-primary-action">
              Pokračovat v tréninku
            </Link>
          ) : todayStatus === 'Completed' || todayStatus === 'Abandoned' ? (
            <div className={'dashboard-hero__closed dashboard-hero__closed--' + todayStatus.toLowerCase()}>
              <strong>{todayStatus === 'Completed' ? 'Dokončeno' : 'Opuštěno'}</strong>
              <Link to={'/workout/' + date} className="button button--quiet dashboard-primary-action">
                Otevřít záznam
              </Link>
            </div>
          ) : (
            <button className="button button--primary dashboard-primary-action" type="button" onClick={() => start.mutate()} disabled={start.isPending || !data.activePlan || todayItems.length === 0}>
              {start.isPending ? 'Zahajuji…' : 'Začít trénink'}
            </button>
          )}
          <small>
            {todayStatus === 'Completed'
              ? 'Uloženo · ' + formatDuration(data.today!.activeDurationSeconds)
              : todayStatus === 'Abandoned'
                ? 'Záznam zůstal bezpečně zachovaný'
                : todayStatus === 'InProgress'
                  ? 'Rozpracovaný trénink je uložený'
                  : 'Postup ukládáme automaticky'}
          </small>
        </div>
      </header>

      {data.calibration && data.calibration.status !== 'Completed' && (
        <section className="notice-band notice-band--important dashboard-calibration">
          <div>
            <span className="section-label">Kalibrace</span>
            <strong>Krátké doladění před startem</strong>
            <p>Chybí {data.calibration.missingQuestionCodes.length} odpovědi, aby coach pracoval s bezpečným výchozím kontextem.</p>
          </div>
          <Link to="/settings" className="button button--quiet">Dokončit</Link>
        </section>
      )}

      <section className="dashboard-section dashboard-schedule" aria-labelledby="dashboard-schedule-title">
        <div className="dashboard-section__heading">
          <h2 id="dashboard-schedule-title"><span aria-hidden="true">02 /</span> Rozpis</h2>
          {hasToday && <span>{workoutStatusLabel(data.today!.status)} / {data.today!.completedSetCount} sérií</span>}
        </div>
        {activePlan.isPending ? (
          <PageLoader label="Načítám dnešní rozpis…" />
        ) : activePlan.isError ? (
          <div className="inline-error" role="alert">Dnešní rozpis se nepodařilo načíst. <button type="button" onClick={() => activePlan.refetch()}>Zkusit znovu</button></div>
        ) : todayItems.length === 0 ? (
          <EmptyState title="Dnes je bez tréninku" text={data.activePlan ? 'Aktivní plán pro dnešek nemá žádné položky.' : 'Nejdřív aktivuj plán.'} />
        ) : (
          <div className="dashboard-schedule__table">
            <div className="dashboard-table-head" aria-hidden="true"><span>#</span><span>Cvik</span><span>Předpis</span><span>Role</span></div>
            {todayItems.map((item, index) => (
              <div className="dashboard-schedule__row" key={item.id}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{item.exerciseName}</strong>
                <span>{formatPrescription(item.axis, item.targets, item.externalLoadKilograms)}</span>
                <small>{item.exposureRole}</small>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="dashboard-context-grid">
        <section className="dashboard-section dashboard-week" aria-labelledby="dashboard-week-title">
          <div className="dashboard-section__heading"><h2 id="dashboard-week-title"><span aria-hidden="true">03 /</span> Týden</h2></div>
          <div className="week-grid" aria-label="Stav aktuálního týdne">
            {week.map((day) => {
              const glyph = day.state === 'done' ? '✓' : day.state === 'active' ? '◐' : day.state === 'abandoned' ? '×' : day.state === 'today' ? '●' : day.state === 'unlogged' ? '?' : day.state === 'future' ? '·' : '—'
              const label = day.state === 'done' ? 'Hotovo' : day.state === 'active' ? 'Rozpracováno' : day.state === 'abandoned' ? 'Opuštěno' : day.state === 'today' ? 'Dnes' : day.state === 'unlogged' ? 'Bez záznamu' : day.planned ? 'Plán' : 'Volno'
              return (
                <div className={`week-day week-day--${day.state}`} key={day.key} title={`${day.short}: ${label}`}>
                  <span>{day.short}</span><b aria-hidden="true">{glyph}</b><small>{label}</small>
                </div>
              )
            })}
          </div>
          <p className="week-legend">✓ hotovo / ◐ rozpracováno / × opuštěno / ● dnes / ? bez záznamu</p>
        </section>

        <section className="dashboard-section dashboard-goals" aria-labelledby="dashboard-goals-title">
          <div className="dashboard-section__heading">
            <h2 id="dashboard-goals-title"><span aria-hidden="true">04 /</span> Cíle</h2>
            <Link to="/plan" className="text-link">Celý plán <ArrowRight size={15} /></Link>
          </div>
          {data.priorityGoals.length === 0 ? (
            <EmptyState title="Cíle zatím nejsou" text="Aktivní plán nemá připojené cílové kontrakty." />
          ) : (
            <div className="dashboard-goal-list">
              {data.priorityGoals.map((goal) => (
                <article className="dashboard-goal-row" key={goal.id}>
                  <div><strong>{goal.name}</strong><span>{goal.priority === 'Primary' ? 'Hlavní cíl' : 'Podpůrný cíl'}</span></div>
                  <b>{goal.milestoneTarget !== null ? `${goal.milestoneTarget}${goal.milestoneAxis === 'HoldSeconds' ? ' s' : ''}` : goal.status === 'Active' ? 'Aktivní' : goal.status}</b>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="dashboard-section dashboard-history" aria-labelledby="dashboard-history-title">
        <div className="dashboard-section__heading">
          <h2 id="dashboard-history-title"><span aria-hidden="true">05 /</span> Poslední záznamy</h2>
          <Link to="/history" className="text-link">Celá historie <ArrowRight size={15} /></Link>
        </div>
        {visibleRecentWorkouts.length === 0 ? (
          <EmptyState title="Historie je zatím prázdná" text="První dokončený trénink se objeví tady." />
        ) : (
          <div className="dashboard-history__table">
            {visibleRecentWorkouts.slice(0, 6).map((workout) => (
              <Link to={`/workout/${workout.localDate}`} key={workout.id} className="dashboard-history__row">
                <time dateTime={workout.localDate}>{formatDate(workout.localDate, { day: 'numeric', month: 'short' })}</time>
                <strong>{workout.status === 'Completed' ? 'Dokončený trénink' : workoutStatusLabel(workout.status)}</strong>
                <span>{workout.exerciseCount} cviků</span>
                <span>{workout.completedSetCount} sérií</span>
                <b>{formatDuration(workout.activeDurationSeconds)}</b>
              </Link>
            ))}
          </div>
        )}
      </section>

      {start.isError && <p className="inline-error">Trénink se nepodařilo připravit. Zkus to prosím znovu.</p>}
    </div>
  )
}
